import React, { useState } from 'react';
import { imageService, idProofImageService } from '../services/api';

function ImageDebugger() {
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const testImageUrls = async () => {
    setLoading(true);
    const results = [];

    try {
      // Test a few sample URLs
      const sampleUrls = [
        'http://localhost:8000/uploads/images/1',
        'http://localhost:8000/uploads/id-proofs/1',
        'http://localhost:8000/api/images/room/1',
        'http://localhost:8000/api/id-proof-images/user/1',
      ];

      for (const url of sampleUrls) {
        try {
          const testResult = await imageService.testImageUrl(url);
          results.push({ url, ...testResult });
        } catch (error) {
          results.push({ 
            url, 
            accessible: false, 
            error: error.message 
          });
        }
      }

      setTestResults(results);
    } catch (error) {
      console.error('Error testing URLs:', error);
    } finally {
      setLoading(false);
    }
  };

  const testImageService = async () => {
    setLoading(true);
    const results = [];

    try {
      // Test room images API
      try {
        const roomImagesResponse = await imageService.getImages('room', 1);
        results.push({
          service: 'Room Images',
          status: 'Success',
          data: roomImagesResponse,
          urls: roomImagesResponse.data?.map(img => img.url) || []
        });
      } catch (error) {
        results.push({
          service: 'Room Images',
          status: 'Error',
          error: error.message
        });
      }

      // Test ID proof images API
      try {
        const idProofResponse = await idProofImageService.getByUser(1);
        results.push({
          service: 'ID Proof Images',
          status: 'Success',
          data: idProofResponse,
          urls: idProofResponse.data?.map(img => img.url) || []
        });
      } catch (error) {
        results.push({
          service: 'ID Proof Images',
          status: 'Error',
          error: error.message
        });
      }

      setTestResults(results);
    } catch (error) {
      console.error('Error testing services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      padding: '24px', 
      backgroundColor: '#F5F3EB', 
      border: '2px solid #E3DFD3', 
      borderRadius: '8px',
      margin: '20px',
      fontFamily: 'monospace'
    }}>
      <h3>Image Debug Tools</h3>
      
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <button
          onClick={testImageUrls}
          disabled={loading}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1A3E2A',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Testing URLs...' : 'Test Image URLs'}
        </button>
        
        <button
          onClick={testImageService}
          disabled={loading}
          style={{
            padding: '8px 16px',
            backgroundColor: '#163626',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Testing APIs...' : 'Test Image APIs'}
        </button>
        
        <button
          onClick={() => setTestResults([])}
          style={{
            padding: '8px 16px',
            backgroundColor: '#5F6B61',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Clear Results
        </button>
      </div>

      {testResults.length > 0 && (
        <div style={{ 
          backgroundColor: '#fff', 
          border: '1px solid #D2CDBF', 
          borderRadius: '6px',
          padding: '16px',
          marginTop: '16px'
        }}>
          <h4>Test Results:</h4>
          {testResults.map((result, index) => (
            <div key={index} style={{ 
              marginBottom: '16px', 
              padding: '12px', 
              backgroundColor: result.accessible === false || result.status === 'Error' ? '#F9EEEB' : '#F0F3EE',
              border: `1px solid ${result.accessible === false || result.status === 'Error' ? '#E5B3A9' : '#C4D4C8'}`,
              borderRadius: '4px'
            }}>
              {result.url && (
                <>
                  <div><strong>URL:</strong> {result.url}</div>
                  <div><strong>Accessible:</strong> {result.accessible ? 'Yes' : 'No'}</div>
                  {result.status && <div><strong>Status:</strong> {result.status}</div>}
                  {result.error && <div style={{color: '#8E3628'}}><strong>Error:</strong> {result.error}</div>}
                  {result.headers && (
                    <details style={{ marginTop: '8px' }}>
                      <summary>Headers</summary>
                      <pre style={{ fontSize: '11px', marginTop: '4px' }}>
                        {JSON.stringify(result.headers, null, 2)}
                      </pre>
                    </details>
                  )}
                </>
              )}
              
              {result.service && (
                <>
                  <div><strong>Service:</strong> {result.service}</div>
                  <div><strong>Status:</strong> {result.status}</div>
                  {result.error && <div style={{color: '#8E3628'}}><strong>Error:</strong> {result.error}</div>}
                  {result.urls && result.urls.length > 0 && (
                    <div>
                      <strong>Image URLs found:</strong>
                      <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                        {result.urls.map((url, urlIndex) => (
                          <li key={urlIndex} style={{ fontSize: '12px' }}>{url}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {result.data && (
                    <details style={{ marginTop: '8px' }}>
                      <summary>Raw Data</summary>
                      <pre style={{ fontSize: '10px', marginTop: '4px', maxHeight: '200px', overflow: 'auto' }}>
                        {JSON.stringify(result.data, null, 2)}
                      </pre>
                    </details>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: '20px', fontSize: '12px', color: '#5F6B61' }}>
        <p><strong>Instructions:</strong></p>
        <ul>
          <li>"Test Image URLs" - Tests sample image URLs for accessibility</li>
          <li>"Test Image APIs" - Tests the actual image service APIs</li>
          <li>Check browser console for additional debug information</li>
          <li>Look for any CORS errors or network failures</li>
        </ul>
      </div>
    </div>
  );
}

export default ImageDebugger;
